/**
 * Authentication Provider
 * LegalSyncUp - Swiss Legal Compliance Platform
 *
 * DUAL AUTH PROVIDER ARCHITECTURE
 * ================================
 * This application supports TWO production-ready authentication providers,
 * selected via the VITE_USE_FIREBASE_AUTH environment variable.
 *
 * PROVIDER SELECTION:
 * -------------------
 * 1. NEON AUTH (default) - Direct authentication
 *    - Uses Better Auth via @neondatabase/neon-js
 *    - Native Cloudflare Workers support
 *    - Shared neon_auth schema with BrandSyncUp monorepo
 *    - Best for: API-first auth, direct sign-in/sign-up
 *
 * 2. FIREBASE AUTH (VITE_USE_FIREBASE_AUTH=true) - Enterprise identity
 *    - Google Cloud Identity Platform integration
 *    - Multi-tenant identity management
 *    - Enterprise SSO (SAML/OIDC)
 *    - Best for: Enterprise customers, Google Workspace, multi-tenant scenarios
 *
 * 3. BETTER AUTH (fallback) - When Neon Auth URL unavailable
 *    - Direct Better Auth connection
 *    - Same schema (neon_auth) as Neon Auth
 *
 * SESSION HANDLING:
 * -----------------
 * - Neon/Better Auth: Cookie-based sessions, 7-day expiry, Worker API validates against neon_auth.session
 * - Firebase Auth: ID tokens, refreshed automatically, verified via Firebase Admin SDK (or REST API in Workers)
 * - Rate limiting applied on both client-side and server-side
 */

import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { authRateLimiter } from '@/utils/rateLimiter';
import { appConfig, isBetterAuthEnabled, isNeonAuthEnabled, isFirebaseAuthEnabled, getAuthProvider } from '@/config/environment';

// NEON AUTH (default): Direct authentication via @neondatabase/neon-js
// Native Cloudflare Workers support, shared auth with BrandSyncUp monorepo
import { NeonAuthProvider, useNeonAuth } from '@/lib/neon-auth';

// BETTER AUTH (fallback): Direct connection when Neon Auth URL unavailable
// Same neon_auth schema, direct Better Auth SDK usage
import { signIn as betterAuthSignIn, signUp as betterAuthSignUp, signOut as betterAuthSignOut, useSession, getSession } from '@/lib/auth/client';

// FIREBASE AUTH (enterprise): Google Identity Platform for multi-tenant/SSO
// Enable via VITE_USE_FIREBASE_AUTH=true for enterprise identity scenarios
import { FirebaseAuthProvider, useFirebaseAuth } from '@/lib/firebase';

// Unified user type that works with both auth systems
interface AuthUser {
  id: string;
  email: string | null;
  name?: string | null;
  emailVerified?: boolean;
  image?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface RateLimitInfo {
  remainingAttempts: number;
  lockedUntil: Date | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  session: unknown;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  rateLimitInfo: RateLimitInfo | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null; rateLimited?: boolean }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  checkAccountLockout: (email: string) => Promise<{ locked: boolean; lockExpiresAt: Date | null }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

/**
 * Better Auth Provider Component
 * Uses the new Better Auth system with Neon database
 */
const BetterAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: session, isPending: loading } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);

  // Get tenant context
  let tenant = null;
  try {
    const tenantContext = useTenant();
    tenant = tenantContext?.tenant;
  } catch {
    // useTenant not available
  }

  // Convert Better Auth user to unified format - memoized to prevent dependency issues
  const user: AuthUser | null = useMemo(() => {
    if (!session?.user) return null;
    return {
      id: session.user.id,
      email: session.user.email,
      name: session.user.name,
      emailVerified: session.user.emailVerified,
      image: session.user.image,
      createdAt: session.user.createdAt?.toString(),
      updatedAt: session.user.updatedAt?.toString(),
    };
  }, [session?.user]);

  const checkAccountLockout = useCallback(async (_email: string): Promise<{ locked: boolean; lockExpiresAt: Date | null }> => {
    // Better Auth handles account lockout internally
    return { locked: false, lockExpiresAt: null };
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<{ error: string | null; rateLimited?: boolean }> => {
    setError(null);

    // Check rate limiting
    const rateLimitCheck = authRateLimiter.check(email);
    if (!rateLimitCheck.allowed) {
      const retryAfter = rateLimitCheck.lockedUntil
        ? Math.ceil((rateLimitCheck.lockedUntil.getTime() - Date.now()) / 60000)
        : 30;
      const errorMsg = `Too many login attempts. Please try again in ${retryAfter} minutes.`;
      setError(errorMsg);
      setRateLimitInfo({
        remainingAttempts: 0,
        lockedUntil: rateLimitCheck.lockedUntil,
      });
      return { error: errorMsg, rateLimited: true };
    }

    try {
      const result = await betterAuthSignIn.email({
        email,
        password,
      });

      if (result.error) {
        const failureResult = authRateLimiter.recordFailure(email);
        setRateLimitInfo({
          remainingAttempts: failureResult.remainingAttempts,
          lockedUntil: failureResult.lockedUntil,
        });

        let errorMessage = result.error.message || 'Sign in failed';
        if (!failureResult.locked && failureResult.remainingAttempts > 0) {
          errorMessage += ` (${failureResult.remainingAttempts} attempts remaining)`;
        } else if (failureResult.locked) {
          errorMessage = 'Account temporarily locked due to too many failed attempts.';
        }

        setError(errorMessage);
        return { error: errorMessage };
      }

      authRateLimiter.recordSuccess(email);
      setRateLimitInfo(null);
      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign in failed';
      setError(errorMessage);
      return { error: errorMessage };
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string): Promise<{ error: string | null }> => {
    setError(null);

    try {
      const result = await betterAuthSignUp.email({
        email,
        password,
        name: email.split('@')[0], // Use email prefix as default name
      });

      if (result.error) {
        setError(result.error.message || 'Sign up failed');
        return { error: result.error.message || 'Sign up failed' };
      }

      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign up failed';
      setError(errorMessage);
      return { error: errorMessage };
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    setError(null);
    setRateLimitInfo(null);
    await betterAuthSignOut();
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    // Force Better Auth client to re-fetch session from /api/auth/get-session.
    // Updates the internal store, which triggers useSession() re-render.
    // Called by AuthCallback after Google OAuth redirect sets the cookie.
    await getSession();
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    session,
    loading,
    error,
    isAuthenticated: !!user,
    rateLimitInfo,
    signIn,
    signUp,
    signOut,
    refresh,
    checkAccountLockout,
  }), [user, session, loading, error, rateLimitInfo, signIn, signUp, signOut, refresh, checkAccountLockout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Legacy Auth Provider Component (Fallback)
 * Uses Better Auth as fallback when no other provider is configured
 */
const LegacyAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Fall back to Better Auth provider
  return <BetterAuthProvider>{children}</BetterAuthProvider>;
};

/**
 * Neon Auth Provider Component (Primary)
 * Uses Neon Auth powered by Better Auth via @neondatabase/neon-js
 */
const NeonAuthProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <NeonAuthProvider>
      <NeonAuthBridge>{children}</NeonAuthBridge>
    </NeonAuthProvider>
  );
};

/**
 * Bridge component to expose Neon Auth context through AuthContext
 */
const NeonAuthBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const neonAuth = useNeonAuth();
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);

  // Wrap sign in with rate limiting
  const signInWithRateLimit = useCallback(async (email: string, password: string): Promise<{ error: string | null; rateLimited?: boolean }> => {
    const rateLimitCheck = authRateLimiter.check(email);
    if (!rateLimitCheck.allowed) {
      const retryAfter = rateLimitCheck.lockedUntil
        ? Math.ceil((rateLimitCheck.lockedUntil.getTime() - Date.now()) / 60000)
        : 30;
      const errorMsg = `Too many login attempts. Please try again in ${retryAfter} minutes.`;
      setRateLimitInfo({
        remainingAttempts: 0,
        lockedUntil: rateLimitCheck.lockedUntil,
      });
      return { error: errorMsg, rateLimited: true };
    }

    try {
      await neonAuth.signInWithEmail(email, password);
      authRateLimiter.recordSuccess(email);
      setRateLimitInfo(null);
      return { error: null };
    } catch (err) {
      const failureResult = authRateLimiter.recordFailure(email);
      setRateLimitInfo({
        remainingAttempts: failureResult.remainingAttempts,
        lockedUntil: failureResult.lockedUntil,
      });
      const errorMessage = err instanceof Error ? err.message : 'Sign in failed';
      return { error: errorMessage };
    }
  }, [neonAuth]);

  // Wrap sign up
  const signUpWrapper = useCallback(async (email: string, password: string): Promise<{ error: string | null }> => {
    try {
      await neonAuth.signUpWithEmail(email, password);
      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign up failed';
      return { error: errorMessage };
    }
  }, [neonAuth]);

  // Wrap sign out
  const signOutWrapper = useCallback(async (): Promise<void> => {
    setRateLimitInfo(null);
    await neonAuth.signOut();
  }, [neonAuth]);

  // Account lockout check (Neon Auth handles this internally)
  const checkAccountLockout = useCallback(async (_email: string): Promise<{ locked: boolean; lockExpiresAt: Date | null }> => {
    return { locked: false, lockExpiresAt: null };
  }, []);

  // Convert Neon Auth user to unified format
  const unifiedUser: AuthUser | null = useMemo(() => {
    if (!neonAuth.user) return null;
    return {
      id: neonAuth.user.id,
      email: neonAuth.user.email ?? null,
      name: neonAuth.user.name,
      emailVerified: neonAuth.user.emailVerified,
      image: neonAuth.user.image,
      createdAt: neonAuth.user.createdAt?.toString(),
      updatedAt: neonAuth.user.updatedAt?.toString(),
    };
  }, [neonAuth.user]);

  const value = useMemo<AuthContextValue>(() => ({
    user: unifiedUser,
    session: neonAuth.session,
    loading: neonAuth.isLoading,
    error: neonAuth.error?.message ?? null,
    isAuthenticated: neonAuth.isAuthenticated,
    rateLimitInfo,
    signIn: signInWithRateLimit,
    signUp: signUpWrapper,
    signOut: signOutWrapper,
    refresh: neonAuth.refreshSession,
    checkAccountLockout,
  }), [unifiedUser, neonAuth.session, neonAuth.isLoading, neonAuth.error, neonAuth.isAuthenticated, rateLimitInfo, signInWithRateLimit, signUpWrapper, signOutWrapper, neonAuth.refreshSession, checkAccountLockout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Firebase Auth Provider Wrapper
 * Bridges Firebase Auth context to unified AuthContext
 */
const FirebaseAuthProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <FirebaseAuthProvider>
      <FirebaseAuthBridge>{children}</FirebaseAuthBridge>
    </FirebaseAuthProvider>
  );
};

/**
 * Bridge component to expose Firebase Auth through AuthContext
 */
const FirebaseAuthBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const firebaseAuth = useFirebaseAuth();

  const value = useMemo<AuthContextValue>(() => ({
    user: firebaseAuth.user,
    session: firebaseAuth.session,
    loading: firebaseAuth.loading,
    error: firebaseAuth.error,
    isAuthenticated: firebaseAuth.isAuthenticated,
    rateLimitInfo: firebaseAuth.rateLimitInfo,
    signIn: firebaseAuth.signIn,
    signUp: firebaseAuth.signUp,
    signOut: firebaseAuth.signOut,
    refresh: firebaseAuth.refresh,
    checkAccountLockout: firebaseAuth.checkAccountLockout,
  }), [firebaseAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Main Auth Provider
 *
 * DUAL AUTH PROVIDER ARCHITECTURE
 * ================================
 * Both providers are PRODUCTION-READY for their intended use cases.
 *
 * Provider Selection:
 * - FIREBASE (VITE_USE_FIREBASE_AUTH=true): Enterprise identity (multi-tenant, SSO, Google Identity)
 * - NEON (default): Direct authentication (API-first, shared monorepo auth)
 * - BETTER (fallback): When Neon Auth URL not configured
 *
 * Session Handling:
 * - Neon/Better: Cookie-based sessions validated via neon_auth.session table
 * - Firebase: ID tokens verified via Firebase Admin SDK or REST API
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authProvider = getAuthProvider();

  // FIREBASE AUTH: Enterprise identity management
  // Enable via VITE_USE_FIREBASE_AUTH=true for multi-tenant/SSO scenarios
  // Uses Google Cloud Identity Platform for enterprise-grade identity
  if (authProvider === 'firebase') {
    console.info('[Auth] Using Firebase Auth for enterprise identity management');
    return <FirebaseAuthProviderWrapper>{children}</FirebaseAuthProviderWrapper>;
  }

  // NEON AUTH (default): Direct authentication via @neondatabase/neon-js
  // Native Cloudflare Workers support, sessions stored in neon_auth.session
  if (authProvider === 'neon') {
    return <NeonAuthProviderWrapper>{children}</NeonAuthProviderWrapper>;
  }

  // BETTER AUTH: Fallback when Neon Auth URL not configured
  // Direct Better Auth connection to neon_auth schema
  if (authProvider === 'better') {
    return <BetterAuthProvider>{children}</BetterAuthProvider>;
  }

  // Legacy fallback (should not be reached)
  return <LegacyAuthProvider>{children}</LegacyAuthProvider>;
};
